import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Home, Sparkles, Heart } from "lucide-react";

const BOOKING_URL = "/book";

const ServicesSection = ({ onBookingClick, onEstimateClick }: { onBookingClick: () => void; onEstimateClick?: () => void }) => {
  const categories = [
    {
      id: "home-organization",
      label: "Home Organization",
      icon: Home,
      title: "Home Organization Services",
      description: "Custom systems for every room — designed to reduce stress, save time, and create lasting order in your Michigan home.",
      services: [
        {
          name: "Home Office & Work Space Setup",
          description: "Desk, files, and tech organized so you can actually focus. Paper management, cord solutions, and Zoom-ready backgrounds.",
          bullets: ["Desk & filing systems", "Tech & cord management", "Zoom background setup", "Supply station organization"],
        },
        {
          name: "Family & Kitchen Organizing",
          description: "Pantry, fridge, and cooking zones organized for busy households. Lunch stations, snack zones, and school paper systems.",
          bullets: ["Pantry & fridge organization", "Lunch & snack station setup", "School paper management", "Meal prep zone design"],
        },
        {
          name: "Closet & Seasonal Rotation",
          description: "Year-round closet editing, seasonal clothing rotation, and donation coordination — not just spring cleaning.",
          bullets: ["Closet edit & organizing", "Seasonal clothing rotation", "Donation bag pickup included", "Wardrobe system design"],
        },
        {
          name: "Moving & Unpacking Support",
          description: "Pre-move decluttering, unpacking and setup, plus a 'first night' essentials box so day one feels like home.",
          bullets: ["Pre-move decluttering", "Unpacking & room setup", "\"First night\" essentials box", "Layout planning & placement"],
        },
      ],
    },
    {
      id: "cleaning",
      label: "Cleaning",
      icon: Sparkles,
      title: "Professional Cleaning Services",
      description: "Residential and commercial cleaning that keeps your spaces spotless, sanitized, and guest-ready — on your schedule.",
      services: [
        {
          name: "Residential Cleaning",
          description: "Weekly, bi-weekly, or monthly deep cleaning for Michigan homes. Eco-friendly products and customized checklists.",
          bullets: ["Weekly/bi-weekly/monthly options", "Deep cleaning & sanitization", "Eco-friendly products available", "Customized cleaning checklist"],
        },
        {
          name: "Commercial Cleaning",
          description: "Offices, churches, medical waiting rooms, and retail spaces kept immaculate with reliable recurring service.",
          bullets: ["Office & retail cleaning", "Medical & church facilities", "Recurring weekly/bi-weekly plans", "After-hours scheduling available"],
        },
        {
          name: "Move In/Out Cleaning",
          description: "Thorough top-to-bottom cleaning for move-ins, move-outs, and real estate showings.",
          bullets: ["Complete deep clean", "Appliance & fixture detailing", "Window & baseboard cleaning", "Same-day service available"],
        },
      ],
    },
    {
      id: "estate-management",
      label: "Estate & Life Transitions",
      icon: Heart,
      title: "Estate & Life Transition Services",
      description: "Compassionate support for life's biggest changes — helping seniors downsize, families transition, and students settle in.",
      services: [
        {
          name: "Senior Downsizing & Estate Help",
          description: "Gentle, respectful support helping seniors sort, donate, and transition to smaller spaces with dignity and care.",
          bullets: ["Compassionate sorting & editing", "Donation & estate sale coordination", "Packing & moving prep", "New space setup & organization"],
        },
        {
          name: "College Student Setup",
          description: "Help students furnish and equip their dorm or first apartment with smart systems that make independent living easy.",
          bullets: ["Dorm & apartment setup", "Storage & closet optimization", "Essentials shopping list", "Study space organization"],
        },
        {
          name: "Real Estate Prep & Staging",
          description: "Strategic decluttering and staging to increase property value and help Michigan homes sell faster.",
          bullets: ["Pre-listing decluttering", "Staging & presentation prep", "Curb appeal coordination", "\"1-Week Market Ready\" option"],
        },
      ],
    },
  ];

  return (
    <section id="services" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <header className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-4">
            Our Services
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Professional organizing, cleaning, and life transition services throughout Michigan — designed for clarity, calm, and lasting results.
          </p>
        </header>

        <Tabs defaultValue={categories[0].id} className="max-w-7xl mx-auto">
          <TabsList className="flex flex-wrap gap-2 justify-center mb-8 h-auto">
            {categories.map((cat) => (
              <TabsTrigger key={cat.id} value={cat.id} className="flex items-center gap-2 px-5 py-3 text-sm">
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map((cat) => (
            <TabsContent key={cat.id} value={cat.id} className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-serif font-medium mb-2">{cat.title}</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">{cat.description}</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cat.services.map((service, idx) => (
                  <Card key={idx} className="border-border hover-lift fade-in-up bg-gradient-to-br from-card to-accent/10 flex flex-col">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-serif font-medium">{service.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                    </CardHeader>
                    <CardContent className="space-y-4 flex-1 flex flex-col">
                      <ul className="space-y-1.5 flex-1">
                        {service.bullets.map((b, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                            <span className="text-sm">{b}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => window.location.href = BOOKING_URL}
                        variant="outline"
                        className="w-full mt-auto"
                        size="sm"
                      >
                        Book {service.name.split(" ")[0]} Help
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button onClick={() => window.location.href = BOOKING_URL} size="lg" className="luxury-gradient text-primary-foreground">
                  Book Free 20-Min Consultation
                </Button>
                <Button variant="outline" size="lg" onClick={() => window.open('sms:+16167270343?body=Hi! I\'d like to learn more about your services.', '_self')}>
                  Text Photos of Your Space
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default ServicesSection;
